import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Plus,
  Search,
  Building2,
  Loader2,
  MoreHorizontal,
  Pencil,
  Trash2,
  Mail,
  Phone,
  MapPin,
  ArrowLeft,
} from 'lucide-react';
import { usePermission } from '@/components/guards/permission-guard';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { VendorModal } from '@/components/trading/consignment/vendor-modal';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';
import { useSessionState } from '@/hooks/use-session-state';
import { cn } from '@/lib/utils';

const statusColors: Record<string, string> = {
  ACTIVE: 'bg-green-100 text-green-700',
  INACTIVE: 'bg-gray-100 text-gray-700',
  PENDING_APPROVAL: 'bg-yellow-100 text-yellow-700',
};

export function ConsignmentVendorsPage() {
  const { t } = useTranslation('trading');
  const { t: tCommon } = useTranslation('common');
  const canCreate = usePermission('CONSIGNMENT', 'CREATE');
  const canEdit = usePermission('CONSIGNMENT', 'EDIT');
  const canDelete = usePermission('CONSIGNMENT', 'DELETE');

  const [debouncedSearch, setDebouncedSearch] = useSessionState(
    'trading:consignment-vendors:debouncedSearch',
    ''
  );
  const [searchQuery, setSearchQuery] = useState(debouncedSearch);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState<any>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [vendorToDelete, setVendorToDelete] = useState<{ id: string; name: string } | null>(null);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setTimeout(() => {
      setDebouncedSearch(value);
    }, 300);
  };

  const { data: vendors, isLoading } = trpc.tradingConsignment.getVendors.useQuery({
    search: debouncedSearch || undefined,
  });

  const utils = trpc.useUtils();

  const deleteMutation = trpc.tradingConsignment.deleteVendor.useMutation({
    onSuccess: () => {
      showSuccessToast(t('consignment.vendors.vendorDeleted'));
      utils.tradingConsignment.getVendors.invalidate();
      utils.tradingConsignment.listVendors.invalidate();
      setDeleteDialogOpen(false);
      setVendorToDelete(null);
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const handleEdit = (vendor: any) => {
    setSelectedVendor(vendor);
    setIsModalOpen(true);
  };

  const handleDelete = (vendor: { id: string; companyName: string }) => {
    setVendorToDelete({ id: vendor.id, name: vendor.companyName });
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (vendorToDelete) {
      deleteMutation.mutate(vendorToDelete.id);
    }
  };

  const handleAddNew = () => {
    setSelectedVendor(null);
    setIsModalOpen(true);
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/trading/consignment"
            className="text-muted-foreground hover:text-foreground inline-flex items-center gap-2 text-sm"
          >
            <ArrowLeft className="h-4 w-4" />
            {t('consignment.list.title')}
          </Link>
          <div>
            <h1 className="text-2xl font-bold">{t('consignment.vendors.title')}</h1>
            <p className="text-muted-foreground">{t('consignment.vendors.subtitle')}</p>
          </div>
        </div>
        {canCreate && (
          <Button onClick={handleAddNew}>
            <Plus className="mr-2 h-4 w-4" />
            {t('consignment.vendors.addVendor')}
          </Button>
        )}
      </div>

      {/* Search */}
      <div className="flex items-center gap-4">
        <div className="relative max-w-sm flex-1">
          <Search className="text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
          <Input
            placeholder={t('consignment.vendors.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {/* Vendors List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="text-primary h-8 w-8 animate-spin" />
          <span className="text-muted-foreground ml-2">{tCommon('loading')}</span>
        </div>
      ) : !vendors || vendors.length === 0 ? (
        <div className="border-border bg-muted/30 flex flex-col items-center justify-center border py-12 text-center">
          <Building2 className="text-muted-foreground/50 mb-4 h-12 w-12" />
          <h3 className="text-lg font-medium">{t('consignment.vendors.noVendors')}</h3>
          <p className="text-muted-foreground mt-1 text-sm">
            {t('consignment.vendors.noVendorsDesc')}
          </p>
          {canCreate && (
            <Button className="mt-4" onClick={handleAddNew}>
              <Plus className="mr-2 h-4 w-4" />
              {t('consignment.vendors.addVendor')}
            </Button>
          )}
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {vendors.map((vendor) => (
            <div key={vendor.id} className="border-border bg-card space-y-4 border p-5">
              {/* Header */}
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <div className="flex h-10 w-10 items-center justify-center bg-blue-100">
                    <Building2 className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold">{vendor.companyName}</h3>
                    {vendor.tradeName && (
                      <p className="text-muted-foreground text-sm">{vendor.tradeName}</p>
                    )}
                  </div>
                </div>
                {(canEdit || canDelete) && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {canEdit && (
                        <DropdownMenuItem onClick={() => handleEdit(vendor)}>
                          <Pencil className="mr-2 h-4 w-4" />
                          {tCommon('edit')}
                        </DropdownMenuItem>
                      )}
                      {canDelete && (
                        <DropdownMenuItem
                          className="text-red-600"
                          onClick={() => handleDelete(vendor)}
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          {tCommon('delete')}
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>

              {/* Contact Info */}
              <div className="space-y-2 text-sm">
                <div className="font-medium">{vendor.contactName}</div>
                <div className="text-muted-foreground flex items-center gap-2">
                  <Mail className="h-3.5 w-3.5" />
                  <a href={`mailto:${vendor.email}`} className="hover:underline">
                    {vendor.email}
                  </a>
                </div>
                <div className="text-muted-foreground flex items-center gap-2">
                  <Phone className="h-3.5 w-3.5" />
                  <a href={`tel:${vendor.phone}`} className="hover:underline">
                    {vendor.phone}
                  </a>
                </div>
                {(vendor.city || vendor.country) && (
                  <div className="text-muted-foreground flex items-center gap-2">
                    <MapPin className="h-3.5 w-3.5" />
                    <span>{[vendor.city, vendor.country].filter(Boolean).join(', ')}</span>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between border-t pt-3">
                <div className="text-sm">
                  <span className="text-muted-foreground">
                    {t('consignment.vendors.commission')}:
                  </span>{' '}
                  <span className="font-medium">{vendor.commissionRate}%</span>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={cn(
                      'px-2 py-0.5 text-xs font-medium',
                      (vendor.isActive ? statusColors['ACTIVE'] : statusColors['INACTIVE']) ||
                        'bg-gray-100 text-gray-700'
                    )}
                  >
                    {t(`consignment.vendors.status.${vendor.isActive ? 'active' : 'inactive'}`)}
                  </span>
                  {vendor.consignmentCount > 0 && (
                    <span className="text-muted-foreground text-xs">
                      {vendor.consignmentCount} {t('consignment.vendors.consignments')}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Vendor Modal */}
      <VendorModal open={isModalOpen} onOpenChange={setIsModalOpen} vendor={selectedVendor} />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('consignment.vendors.deleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('consignment.vendors.deleteConfirm', { name: vendorToDelete?.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>
              {tCommon('cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleteMutation.isPending}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {tCommon('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
